import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import {
  Actor,
  BaseMessage,
  BotDetails,
  Chat,
  ChatMessageWithAssistant,
  ChatMessageWithClient,
  Client,
  ClientProfile,
  ModelTag,
  PayloadMessage,
  TraineeBubble,
  TraineeBubbleInfo,
} from '../../types';
import { checkResponse, fetchWithAuth } from '../../util';
import {
  delClient,
  selectActors,
  selectCurrentClient,
  setClient,
  setClients,
  setClientProfile,
  setActors,
  setCurrentClient,
} from '../client/clientSlice';
import { AppThunk } from '../store';
import {
  AssistantMessageDto,
  AssistantMessageScoreDto,
  BotDetailsDto,
  ChatWithMessagesDto,
  ClientMessageDto,
  QueryActorReqDto,
  QueryAssistantReqDto,
  ClientProfileDto,
  ClientProfileRequest,
  ClientMessageSentimentReqDto,
  ClientMessageSentimentResDto,
  ActorProfileDto,
  ClientMessageSuicidalityReqDto,
  ClientMessageSuicidalityResDto,
} from './chatDto';
import {
  clearMessagesWithAssistant,
  clearMessagesWithClient,
  createChat,
  delChat,
  pushAssistantMessage,
  pushClientMessage,
  pushLetsTalkAssistantMessage,
  pushLetsTalkUserToAssistantMessage,
  pushUserToAssistantMessage,
  pushUserToClientMessage,
  selectCurrentChat,
  selectCurrentChatInput,
  selectLetsTalkInputWithAssistant,
  selectLetsTalkMessagesWithAssistant,
  selectLetsTalkMessagesWithClient,
  selectMessagesWithAssistant,
  selectMessagesWithClient,
  setAssistantMessageScores,
  setBotDetails,
  setChat,
  setChats,
  setClientMessageSentiments,
  setSentimentProcessing,
  setInputWithAssistant,
  setLetsTalkInputWithAssistant,
  setClientMessageSuicidality,
  setSuicidalityProcessing,
  setAssistantMessageScore,
  setActorResponseProcessingChatId,
} from './chatSlice';
import {
  selectActorConfig,
  selectAssistantConfig,
  selectSettingsDeveloper,
} from '../settings/settingsSlice';
import {
  selectSavedChatsDto,
  setChatBubbles,
  setCtFeedback,
  setSavedChats,
  setSelectedChat,
} from '../saved-chats/savedChatsSlice';
import { BubbleDto, SavedChatDto } from '../saved-chats/savedChatsDto';
import { selectUserAuth } from '../auth/authSlice';
import { deleteRiskOfChat } from '../assistant/assistantSlice';
const BASE_URL = process.env.REACT_APP_BACKEND_URL;
const ACTOR_URL = new URL('chat/actor', BASE_URL);
const ASSISTANT_URL = new URL('chat/assistant', BASE_URL);
const CHATS_URL = new URL('chat', BASE_URL);
const ACTORS_URL = new URL('actor/profile', BASE_URL);
const MESSAGE_SCORE_URL = new URL('message/assistant/score', BASE_URL);
const MESSAGE_SENTIMENT_URL = new URL('message/client/sentiment', BASE_URL);
const MASKED_MESSAGE_URL = new URL('chat/masked/message', BASE_URL);
const MESSAGE_SUICIDALITY_URL = new URL('message/client/suicidality', BASE_URL);
const BUBBLES_URL = new URL('chat/bubbles', BASE_URL);
const BUBBLE_INFO_URL = new URL('chat/bubble-info', BASE_URL);

const BREAK_REGEX = /\s*<break>\s*/;

export const TOAST_NO_MESSAGES = 'There are no messages yet!';
const TOAST_NO_INPUT = 'Your message input is needed!';
const TOAST_ARCHIVED = 'Conversation archived!';
const TOAST_SENTIMENT_FAILED = 'Failed to evaluate message sentiment!';

export const CHAT_SAVE_TO_DATABASE_FAILED =
  'Failed to save the chat to database!';

const LABEL_ASSISTANT = 'Affected person';
const LABEL_USER = 'Volunteer';

/**
 * High typing speed so that the user does not wait too long,
 * in characters per minute.
 * Obtained from
 * https://www.typingpal.com/en/documentation/school-edition/pedagogical-resources/typing-speed
 */
const TYPING_CPM = 350;
/**
 * Random variation in typing duration, in milliseconds.
 */
const TYPING_VAR = 200;
/**
 * Maximum duration to type a message, in milliseconds.
 */
const TYPING_MAX_MS = 5000;

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Serializes messages delimited by new lines.
 *
 * @param messages Messages to serialize.
 * @param labels Optional assistant and user role labels.
 * @returns Serialized string.
 */
export function serializeMessages(
  messages: BaseMessage[],
  labels?: { assistant?: string; user?: string }
) {
  return messages
    .map(
      (m) =>
        `${
          m.role === 'assistant'
            ? labels?.assistant || LABEL_ASSISTANT
            : labels?.user || LABEL_USER
        }: ${m.content}`
    )
    .join('\n\n');
}

/**
 * Reads the next chunk from the given reader.
 *
 * @param reader Reader to read from.
 * @returns `{ done: boolean; chunk: string }`
 *
 * `done`: whether the reader has been exhausted;
 * `chunk`: decoded string.
 */
async function readNext(reader: ReadableStreamDefaultReader<Uint8Array>) {
  const { done, value } = await reader.read();
  const chunk = new TextDecoder().decode(value);
  return { done, chunk };
}

/**
 * Streams string chunks from the given reader until exhaustion.
 *
 * @param reader Reader to stream from.
 */
async function* streamAnswer(
  reader: ReadableStreamDefaultReader<Uint8Array>
): AsyncGenerator<string, void, void> {
  let done = false;
  let chunk = '';
  while (!done) {
    ({ done, chunk } = await readNext(reader));
    yield chunk;
  }
}

/**
 * Separates possibly concatenated JSON object chunks into individual chunks.
 * e.g. '{"answer": "hello"}{"answer": "world"}'
 * -> '{"answer": "hello"}', '{"answer": "world"}'
 *
 * @param chunks Possibly multiple concatenated chunks.
 * @returns List of chunks.
 */
function splitChunks(chunks: string) {
  return chunks
    .split(/\}\s*\{/)
    .join('}{}{')
    .split('{}')
    .filter((c) => !!c.trim());
}

/**
 * Parses a JSON string into an object, if parseable.
 * @param chunk JSON string.
 * @returns `Object` or `undefined`.
 */
function parseChunk(chunk: string) {
  try {
    return JSON.parse(chunk);
  } catch (e) {
    console.error(e);
  }
}

export const querySelfPlay = (): AppThunk => async (dispatch, getState) => {
  const state = getState();
  const client = selectCurrentClient(state);
  const messagesWithClient = selectMessagesWithClient(state);
  const messagesWithAssistant = selectMessagesWithAssistant(state);
  if (!client) {
    return;
  }
  const incomingMessages = messagesWithClient.filter(
    (m) => m.role === 'assistant'
  );
  const n = incomingMessages.length;
  if (n < 1) {
    return;
  }
  const content = incomingMessages[n - 1].content;

  dispatch(
    pushUserToAssistantMessage({
      messageId: uuidv4(),
      userId: client.userId,
      payload: content,
      promptType: 'generate_response',
    })
  );
  dispatch(
    queryDialogueAssistant(
      {
        prompt_type: 'generate_response',
        content,
        history_assistant: messagesWithAssistant,
        history_client: messagesWithClient,
        persist: client.userType === 'client',
        chat_id: client.userId,
      },
      client.userId
    )
  );
};

export const queryActor =
  (
    query: QueryActorReqDto,
    hasTyping: boolean = true,
    isSelfPlay: boolean = false,
    isCustomActor: boolean = false,
    isTrainee: boolean = false
  ): AppThunk =>
  async (dispatch, getState) => {
    const config = selectActorConfig(getState());
    const userAuth = selectUserAuth(getState());
    const currentChatId = selectCurrentChat(getState())?.chatId;
    if (currentChatId) {
      dispatch(setActorResponseProcessingChatId(currentChatId));
    }

    try {
      const resBody =
        (config.actorProfile && !isCustomActor) || isTrainee
          ? JSON.stringify({
              ...query,
              llm_tag: config.modelTag,
              actor_profile: true,
              username: userAuth?.username,
            })
          : JSON.stringify({
              ...query,
              llm_tag: config.modelTag,
              actor_profile: false,
              username: userAuth?.username,
            });

      const res = await checkResponse(
        fetchWithAuth(ACTOR_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: resBody,
        }),
        { toastOnApiError: true, toastOnFetchError: true }
      );
      const reader = res?.body?.getReader();
      if (!reader) {
        return;
      }

      let messagePayload: PayloadMessage | null = null;
      for await (const chunks of streamAnswer(reader)) {
        for (const chunk of splitChunks(chunks)) {
          const data = parseChunk(chunk);
          if (data && 'error_code' in data) {
            toast.error(data['error']);
            return;
          }
          if (data && 'actor' in data && 'answer' in data) {
            messagePayload = messagePayload || {
              messageId: uuidv4(),
              userId: data['actor'],
              payload: '',
              user: userAuth?.username,
            };
            if (messagePayload.userId !== data['actor']) {
              // dispatch userId-mismatched payload
              dispatch(pushClientMessage(messagePayload));
              messagePayload = {
                messageId: uuidv4(),
                userId: data['actor'],
                payload: '',
                user: userAuth?.username,
              };
            }

            const { payload, ...others } = messagePayload;
            const parts = (payload + data['answer']).split(BREAK_REGEX);
            // dispatch all but the last part
            for (const part of parts.slice(0, parts.length - 1)) {
              const messageId = uuidv4();
              // empty payload creates loading indicator
              dispatch(
                pushClientMessage({ ...others, messageId, payload: '' })
              );
              // sleep to simulate typing
              if (hasTyping) {
                // duration in seconds
                let duration = part.length / TYPING_CPM;
                duration *= 60000; // convert to milliseconds
                duration += Math.random() * TYPING_VAR;
                await sleep(Math.min(TYPING_MAX_MS, duration));
              }
              dispatch(
                pushClientMessage({ ...others, messageId, payload: part })
              );
            }
            messagePayload.messageId = uuidv4();
            messagePayload.payload = parts[parts.length - 1];
          }
        }
      }
      // dispatch last payload, if present
      if (messagePayload) {
        messagePayload.messageId = uuidv4();
        messagePayload.user = userAuth?.username;
        // empty payload creates loading indicator
        dispatch(pushClientMessage({ ...messagePayload, payload: '' }));
        // random 1-2s sleep to simulate typing
        if (hasTyping) {
          await sleep(Math.random() * 1000 + 1000);
        }
        dispatch(pushClientMessage(messagePayload));
      }

      if (isSelfPlay) {
        dispatch(querySelfPlay());
      }
    } finally {
      if (
        currentChatId &&
        selectCurrentChat(getState())?.chatId === currentChatId
      ) {
        dispatch(setActorResponseProcessingChatId(null));
      }
    }
  };

async function maskHistory(query: QueryAssistantReqDto) {
  let masked: BaseMessage[] = [];

  if (query.history_client) {
    for (const history of query.history_client) {
      if (history.role === 'assistant') {
        const maskedRes = await checkResponse(
          fetchWithAuth(MASKED_MESSAGE_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ input_text: history.content }),
          }),
          { toastOnApiError: true, toastOnFetchError: true }
        );

        if (maskedRes) {
          const data = await maskedRes.json();
          const maskedHistory: BaseMessage = {
            ...history,
            content: data.output_text,
          };
          masked.push(maskedHistory);
        }
      }
    }
  }

  return masked;
}

async function queryAssistant(
  query: QueryAssistantReqDto,
  modelTag?: ModelTag,
  maskClientMessage?: boolean,
  // @ts-ignore
  getState
) {
  const userAuth = selectUserAuth(getState());
  if (maskClientMessage) {
    const data = await maskHistory(query);

    if (data.length > 0) {
      query.content = data[data.length - 1].content;
      query.history_client = data;
    }

    const res = await checkResponse(
      fetchWithAuth(ASSISTANT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...query,
          llm_tag: modelTag,
          username: userAuth?.username,
        }),
      }),
      { toastOnApiError: true, toastOnFetchError: true }
    );

    return res?.body?.getReader();
  } else {
    const res = await checkResponse(
      fetchWithAuth(ASSISTANT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...query,
          llm_tag: modelTag,
          username: userAuth?.username,
        }),
      }),
      { toastOnApiError: true, toastOnFetchError: true }
    );

    return res?.body?.getReader();
  }
}

export const queryDialogueAssistant =
  (query: QueryAssistantReqDto, selfPlayActor?: string): AppThunk =>
  async (dispatch, getState) => {
    const modelTag =
      query.prompt_type === 'generate_response'
        ? selectAssistantConfig(getState()).proposeResponseModelTag
        : selectAssistantConfig(getState()).modelTag;
    query.withExplanationState =
      selectSettingsDeveloper(getState()).includeExplanationState;
    const reader = await queryAssistant(
      query,
      modelTag,
      selectSettingsDeveloper(getState()).maskClientMessage,
      getState
    );
    if (!reader) {
      return;
    }
    const timestamp = new Date().toISOString();

    const selfPlayPayload: PayloadMessage = {
      payload: '',
      userId: selfPlayActor || '',
      messageId: uuidv4(),
    };
    for await (const chunks of streamAnswer(reader)) {
      for (const chunk of splitChunks(chunks)) {
        const data = parseChunk(chunk);
        if (data && 'error_code' in data) {
          toast.error(data['error']);
          return;
        }

        if (
          data &&
          'chat_id' in data &&
          'message_id' in data &&
          'answer' in data
        ) {
          selfPlayPayload.payload += data['answer'];

          dispatch(
            pushAssistantMessage({
              userId: data['chat_id'],
              payload: data['answer'],
              messageId: data['message_id'],
              modelTag,
              timestamp,
            })
          );
        }
      }
    }

    if (selfPlayActor) {
      dispatch(pushUserToClientMessage(selfPlayPayload));
    }
  };

export const queryLetsTalkAssistant =
  (query: QueryAssistantReqDto): AppThunk =>
  async (dispatch, getState) => {
    const config = selectAssistantConfig(getState());
    query.withExplanationState =
      selectSettingsDeveloper(getState()).includeExplanationState;
    const reader = await queryAssistant(
      query,
      config.modelTag,
      false,
      getState
    );
    if (!reader) {
      return;
    }
    const timestamp = new Date().toISOString();
    for await (const chunks of streamAnswer(reader)) {
      for (const chunk of splitChunks(chunks)) {
        const data = parseChunk(chunk);
        if (data && 'message_id' in data && 'answer' in data) {
          dispatch(
            pushLetsTalkAssistantMessage({
              userId: '', // ignored
              messageId: data['message_id'],
              payload: data['answer'],
              timestamp,
            })
          );
        }
      }
    }
  };

export const directChat =
  (content: string): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const client = selectCurrentClient(state);
    const messagesWithAssistant = selectMessagesWithAssistant(state);
    const messagesWithClient = selectMessagesWithClient(state);

    if (!client) {
      return;
    }
    dispatch(
      queryDialogueAssistant({
        prompt_type: 'direct',
        content,
        history_assistant: messagesWithAssistant,
        history_client: messagesWithClient,
        persist: client.userType === 'client',
        chat_id: client.userId,
      })
    );
  };

export const generateResponse = (): AppThunk => async (dispatch, getState) => {
  const state = getState();
  const client = selectCurrentClient(state);
  const messagesWithClient = selectMessagesWithClient(state);
  if (!client) {
    return;
  }
  const incomingMessages = messagesWithClient.filter(
    (m) => m.role === 'assistant'
  );

  const n = incomingMessages.length;
  if (n === 0) {
    toast.warn(TOAST_NO_MESSAGES);
    return;
  }

  const content = incomingMessages[n - 1].content.trim();

  dispatch(
    pushUserToAssistantMessage({
      messageId: uuidv4(),
      userId: client.userId,
      payload: content,
      promptType: 'generate_response',
    })
  );
  dispatch(
    queryDialogueAssistant({
      prompt_type: 'generate_response',
      content,
      history_client: messagesWithClient,
      persist: client.userType === 'client',
      chat_id: client.userId,
    })
  );
};

export const generateWrapUp = (): AppThunk => async (dispatch, getState) => {
  const state = getState();
  const client = selectCurrentClient(state);
  const messagesWithClient = selectMessagesWithClient(state);
  if (!client) {
    return;
  }
  const n = messagesWithClient.length;
  if (n === 0) {
    toast.warn(TOAST_NO_MESSAGES);
    return;
  }

  const content = `${n} messages`;
  dispatch(
    pushUserToAssistantMessage({
      messageId: uuidv4(),
      userId: client.userId,
      payload: content,
      promptType: 'generate_wrap_up',
    })
  );
  dispatch(
    queryDialogueAssistant({
      prompt_type: 'generate_wrap_up',
      content,
      history_client: messagesWithClient,
      persist: client.userType === 'client',
      chat_id: client.userId,
    })
  );
};

export const generateProblemIdentification =
  (): AppThunk => async (dispatch, getState) => {
    const state = getState();
    const client = selectCurrentClient(state);
    const messagesWithClient = selectMessagesWithClient(state);
    if (!client) {
      return;
    }
    const incomingMessages = messagesWithClient.filter(
      (m) => m.role === 'assistant'
    );
    const n = incomingMessages.length;
    if (n === 0) {
      toast.warn(TOAST_NO_MESSAGES);
      return;
    }
    const content = incomingMessages[n - 1].content.trim();

    dispatch(
      pushUserToAssistantMessage({
        messageId: uuidv4(),
        userId: client.userId,
        payload: content,
        promptType: 'generate_problem_identification',
      })
    );
    dispatch(
      queryDialogueAssistant({
        prompt_type: 'generate_problem_identification',
        content,
        history_client: messagesWithClient,
        persist: client.userType === 'client',
        chat_id: client.userId,
      })
    );
  };

export const generateAttemptedSolutions =
  (): AppThunk => async (dispatch, getState) => {
    const state = getState();
    const client = selectCurrentClient(state);
    const messagesWithClient = selectMessagesWithClient(state);
    if (!client) {
      return;
    }
    const incomingMessages = messagesWithClient.filter(
      (m) => m.role === 'assistant'
    );
    const n = incomingMessages.length;
    if (n === 0) {
      toast.warn(TOAST_NO_MESSAGES);
      return;
    }
    const content = incomingMessages[n - 1].content.trim();

    dispatch(
      pushUserToAssistantMessage({
        messageId: uuidv4(),
        userId: client.userId,
        payload: content,
        promptType: 'generate_attempted_solutions',
      })
    );
    dispatch(
      queryDialogueAssistant({
        prompt_type: 'generate_attempted_solutions',
        content,
        history_client: messagesWithClient,
        persist: client.userType === 'client',
        chat_id: client.userId,
      })
    );
  };

export const generateDesiredOutcome =
  (): AppThunk => async (dispatch, getState) => {
    const state = getState();
    const client = selectCurrentClient(state);
    const messagesWithClient = selectMessagesWithClient(state);
    if (!client) {
      return;
    }
    const incomingMessages = messagesWithClient.filter(
      (m) => m.role === 'assistant'
    );
    const n = incomingMessages.length;
    if (n === 0) {
      toast.warn(TOAST_NO_MESSAGES);
      return;
    }
    const content = incomingMessages[n - 1].content.trim();

    dispatch(
      pushUserToAssistantMessage({
        messageId: uuidv4(),
        userId: client.userId,
        payload: content,
        promptType: 'generate_desired_outcome',
      })
    );
    dispatch(
      queryDialogueAssistant({
        prompt_type: 'generate_desired_outcome',
        content,
        history_client: messagesWithClient,
        persist: client.userType === 'client',
        chat_id: client.userId,
      })
    );
  };

export const generateInterventions =
  (): AppThunk => async (dispatch, getState) => {
    const state = getState();
    const client = selectCurrentClient(state);
    const messagesWithClient = selectMessagesWithClient(state);
    if (!client) {
      return;
    }
    const incomingMessages = messagesWithClient.filter(
      (m) => m.role === 'assistant'
    );
    const n = incomingMessages.length;
    if (n === 0) {
      toast.warn(TOAST_NO_MESSAGES);
      return;
    }
    const content = incomingMessages[n - 1].content.trim();

    dispatch(
      pushUserToAssistantMessage({
        messageId: uuidv4(),
        userId: client.userId,
        payload: content,
        promptType: 'generate_interventions',
      })
    );
    dispatch(
      queryDialogueAssistant({
        prompt_type: 'generate_interventions',
        content,
        history_client: messagesWithClient,
        persist: client.userType === 'client',
        chat_id: client.userId,
      })
    );
  };

export const generateC3PGuide = (): AppThunk => async (dispatch, getState) => {
  const state = getState();
  const client = selectCurrentClient(state);
  const messagesWithClient = selectMessagesWithClient(state);
  if (!client) {
    return;
  }
  const incomingMessages = messagesWithClient.filter(
    (m) => m.role === 'assistant'
  );
  const n = incomingMessages.length;
  if (n === 0) {
    toast.warn(TOAST_NO_MESSAGES);
    return;
  }

  const content = incomingMessages[n - 1].content.trim();

  dispatch(
    pushUserToAssistantMessage({
      messageId: uuidv4(),
      userId: client.userId,
      payload: content,
      promptType: 'generate_C3P_guide',
    })
  );
  dispatch(
    queryDialogueAssistant({
      prompt_type: 'generate_C3P_guide',
      content,
      history_client: messagesWithClient,
      persist: client.userType === 'client',
      chat_id: client.userId,
    })
  );
};

export const generateDirections =
  (): AppThunk => async (dispatch, getState) => {
    const state = getState();
    const client = selectCurrentClient(state);
    const messagesWithClient = selectMessagesWithClient(state);
    if (!client) {
      return;
    }
    const n = messagesWithClient.length;
    if (n === 0) {
      toast.warn(TOAST_NO_MESSAGES);
      return;
    }
    const content = `${n} messages`;
    dispatch(
      pushUserToAssistantMessage({
        messageId: uuidv4(),
        userId: client.userId,
        payload: content,
        promptType: 'generate_directions',
      })
    );
    dispatch(
      queryDialogueAssistant({
        prompt_type: 'generate_directions',
        content,
        history_client: messagesWithClient,
        persist: client.userType === 'client',
        chat_id: client.userId,
      })
    );
  };

export const reasonsForLiving = (): AppThunk => async (dispatch, getState) => {
  const state = getState();
  const client = selectCurrentClient(state);
  const messagesWithClient = selectMessagesWithClient(state);
  if (!client) {
    return;
  }
  const n = messagesWithClient.length;
  if (n === 0) {
    toast.warn(TOAST_NO_MESSAGES);
    return;
  }
  const content = `${n} messages`;
  dispatch(
    pushUserToAssistantMessage({
      messageId: uuidv4(),
      userId: client.userId,
      payload: content,
      promptType: 'reasons_for_living',
    })
  );
  dispatch(
    queryDialogueAssistant({
      prompt_type: 'reasons_for_living',
      content,
      history_client: messagesWithClient,
      persist: client.userType === 'client',
      chat_id: client.userId,
    })
  );
};

export const generateResource = (): AppThunk => async (dispatch, getState) => {
  const state = getState();
  const client = selectCurrentClient(state);
  const messagesWithClient = selectMessagesWithClient(state);
  if (!client) {
    return;
  }
  const incomingMessages = messagesWithClient.filter(
    (m) => m.role === 'assistant'
  );
  const n = incomingMessages.length;
  if (n === 0) {
    toast.warn(TOAST_NO_MESSAGES);
    return;
  }
  const content = incomingMessages[n - 1].content;

  dispatch(
    pushUserToAssistantMessage({
      messageId: uuidv4(),
      userId: client.userId,
      payload: content,
      promptType: 'recommend_resource',
    })
  );
  dispatch(
    queryDialogueAssistant({
      prompt_type: 'recommend_resource',
      content,
      history_client: messagesWithClient,
      persist: client.userType === 'client',
      chat_id: client.userId,
    })
  );
};

export const analyseClientHistory_CT_supervision =
  (): AppThunk => async (dispatch, getState) => {
    const state = getState();
    const client = selectCurrentClient(state);
    const messagesWithClient = selectMessagesWithClient(state);
    if (!client) {
      return;
    }
    const n = messagesWithClient.length;
    if (n === 0) {
      toast.warn(TOAST_NO_MESSAGES);
      return;
    }
    const content = `${n} messages`;
    dispatch(
      pushUserToAssistantMessage({
        messageId: uuidv4(),
        userId: client.userId,
        payload: content,
        promptType: 'generate_feedback_CT_supervision',
      })
    );
    dispatch(
      queryDialogueAssistant({
        prompt_type: 'generate_feedback_CT_supervision',
        content,
        history_client: messagesWithClient,
        persist: client.userType === 'client',
        chat_id: client.userId,
      })
    );
  };

export const generate_trainee_feedback =
  (query: QueryAssistantReqDto): AppThunk =>
  async (dispatch, getState) => {
    const modelTag = selectAssistantConfig(getState()).modelTag;
    const url = new URL(`chat/bubbles/${query.chat_id}`, BASE_URL);
    const res = await checkResponse(fetchWithAuth(url), {
      toastOnApiError: true,
      toastOnFetchError: true,
    });
    if (res) {
      const data: BubbleDto[] = await res.json();
      let history_client: BaseMessage[] = [];
      data.forEach((chatBubble) => {
        history_client.push({
          // @ts-ignore: role is always of type MessageRole
          role: chatBubble.role,
          content: chatBubble.content,
        });
      });

      query.content = `${history_client.length} messages`;
      query.history_client = history_client;
      query.withExplanationState =
        selectSettingsDeveloper(getState()).includeExplanationState;
      const reader = await queryAssistant(
        query,
        modelTag,
        selectSettingsDeveloper(getState()).maskClientMessage,
        getState
      );
      if (!reader) {
        return;
      }

      let payload: string = '';

      for await (const chunks of streamAnswer(reader)) {
        for (const chunk of splitChunks(chunks)) {
          const data = parseChunk(chunk);
          if (
            data &&
            'chat_id' in data &&
            'message_id' in data &&
            'answer' in data
          ) {
            payload += data['answer'];
            dispatch(setCtFeedback(payload));
          }
        }
      }
    }
  };

export const analyseClientHistory_CCSR =
  (): AppThunk => async (dispatch, getState) => {
    const state = getState();
    const client = selectCurrentClient(state);
    const messagesWithClient = selectMessagesWithClient(state);
    if (!client) {
      return;
    }
    const n = messagesWithClient.length;
    if (n === 0) {
      toast.warn(TOAST_NO_MESSAGES);
      return;
    }
    const content = `${n} messages`;
    dispatch(
      pushUserToAssistantMessage({
        messageId: uuidv4(),
        userId: client.userId,
        payload: content,
        promptType: 'generate_feedback_CCSR',
      })
    );
    dispatch(
      queryDialogueAssistant({
        prompt_type: 'generate_feedback_CCSR',
        content,
        history_client: messagesWithClient,
        persist: client.userType === 'client',
        chat_id: client.userId,
      })
    );
  };

export const analyseClientHistory_FIST =
  (): AppThunk => async (dispatch, getState) => {
    const state = getState();
    const client = selectCurrentClient(state);
    const messagesWithClient = selectMessagesWithClient(state);
    if (!client) {
      return;
    }
    const n = messagesWithClient.length;
    if (n === 0) {
      toast.warn(TOAST_NO_MESSAGES);
      return;
    }
    const content = `${n} messages`;
    dispatch(
      pushUserToAssistantMessage({
        messageId: uuidv4(),
        userId: client.userId,
        payload: content,
        promptType: 'generate_feedback_FIST',
      })
    );
    dispatch(
      queryDialogueAssistant({
        prompt_type: 'generate_feedback_FIST',
        content,
        history_client: messagesWithClient,
        persist: client.userType === 'client',
        chat_id: client.userId,
      })
    );
  };

export const analyseClientHistory_TES =
  (): AppThunk => async (dispatch, getState) => {
    const state = getState();
    const client = selectCurrentClient(state);
    const messagesWithClient = selectMessagesWithClient(state);
    if (!client) {
      return;
    }
    const n = messagesWithClient.length;
    if (n === 0) {
      toast.warn(TOAST_NO_MESSAGES);
      return;
    }
    const content = `${n} messages`;
    dispatch(
      pushUserToAssistantMessage({
        messageId: uuidv4(),
        userId: client.userId,
        payload: content,
        promptType: 'generate_feedback_TES',
      })
    );
    dispatch(
      queryDialogueAssistant({
        prompt_type: 'generate_feedback_TES',
        content,
        history_client: messagesWithClient,
        persist: client.userType === 'client',
        chat_id: client.userId,
      })
    );
  };

export const summariseClientHistory =
  (): AppThunk => async (dispatch, getState) => {
    const state = getState();
    const client = selectCurrentClient(state);
    const messagesWithClient = selectMessagesWithClient(state);
    if (!client) {
      return;
    }
    const n = messagesWithClient.length;
    if (n === 0) {
      toast.warn(TOAST_NO_MESSAGES);
      return;
    }
    const content = `${n} messages`;
    dispatch(
      pushUserToAssistantMessage({
        messageId: uuidv4(),
        userId: client.userId,
        payload: content,
        promptType: 'summarize',
      })
    );
    dispatch(
      queryDialogueAssistant({
        prompt_type: 'summarize',
        content,
        history_client: messagesWithClient,
        persist: client.userType === 'client',
        chat_id: client.userId,
      })
    );
  };

export const summarisePostClientHistory =
  (): AppThunk => async (dispatch, getState) => {
    const state = getState();
    const client = selectCurrentClient(state);
    const messagesWithClient = selectMessagesWithClient(state);
    if (!client) {
      return;
    }
    const n = messagesWithClient.length;
    if (n === 0) {
      toast.warn(TOAST_NO_MESSAGES);
      return;
    }
    const content = `${n} messages`;
    dispatch(
      pushUserToAssistantMessage({
        messageId: uuidv4(),
        userId: client.userId,
        payload: content,
        promptType: 'summarize_post',
      })
    );
    dispatch(
      queryDialogueAssistant({
        prompt_type: 'summarize_post',
        content,
        history_client: messagesWithClient,
        persist: client.userType === 'client',
        chat_id: client.userId,
      })
    );
  };

export const rewriteEmpathetically =
  (): AppThunk => async (dispatch, getState) => {
    const state = getState();
    const client = selectCurrentClient(state);
    const chat = selectCurrentChat(state);
    const chatInput = selectCurrentChatInput(state);
    const messagesWithClient = selectMessagesWithClient(state);
    if (!client || !chat) {
      return;
    }
    const content = chatInput?.inputWithAssistant.trim();
    if (!content) {
      toast.warn(TOAST_NO_INPUT);
      return;
    }

    dispatch(
      pushUserToAssistantMessage({
        messageId: uuidv4(),
        userId: client.userId,
        payload: content,
        promptType: 'empathetic_rewrite',
      })
    );
    dispatch(setInputWithAssistant({ userId: client.userId, payload: '' }));
    dispatch(
      queryDialogueAssistant({
        prompt_type: 'empathetic_rewrite',
        content,
        history_client: messagesWithClient,
        persist: client.userType === 'client',
        chat_id: client.userId,
      })
    );
  };

export const evaluateClientMessageSentiment =
  (messageId: string): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const client = selectCurrentClient(state);
    const messagesWithClient = selectMessagesWithClient(state);
    if (!client) {
      return;
    }
    if (messagesWithClient.length === 0) {
      toast.warn(TOAST_NO_MESSAGES);
      return;
    }
    const message = messagesWithClient
      .filter((m) => m.role === 'assistant' && m.messageId === messageId)
      .pop();
    const history = messagesWithClient
      .filter((m) => m.role === 'assistant')
      .reduce(
        (acc, m) => {
          if (acc.isSkipped) {
            return acc;
          }
          return {
            value: `${acc.value}\n\n${m.content.trim()}`,
            isSkipped: m.messageId === messageId,
          };
        },
        { value: '', isSkipped: false }
      )
      .value.trim();
    if (!message) {
      // unable to find message with the given messageId
      toast.error(TOAST_SENTIMENT_FAILED);
      return;
    }
    const data: ClientMessageSentimentReqDto = {
      message_id: messageId,
      content: message.content,
      history,
    };
    dispatch(
      setSentimentProcessing({
        userId: client.userId,
        messageId,
        sentimentProcessing: true,
      })
    );
    const res = await checkResponse(
      fetchWithAuth(MESSAGE_SENTIMENT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }),
      { toastOnApiError: true, toastOnFetchError: true }
    );
    if (res) {
      const body: ClientMessageSentimentResDto = await res.json();
      dispatch(
        setClientMessageSentiments({
          userId: client.userId,
          messageId,
          contentSentiment: body.content_sentiment,
          historySentiment: body.history_sentiment,
        })
      );
    }
  };

export const evaluateClientMessageSuicidality =
  (messageId: string): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const client = selectCurrentClient(state);
    const messagesWithClient = selectMessagesWithClient(state);
    if (!client) {
      return;
    }

    if (messagesWithClient.length === 0) {
      toast.warn(TOAST_NO_MESSAGES);
      return;
    }

    const message = messagesWithClient
      .filter((m) => m.role === 'assistant' && m.messageId === messageId)
      .pop();

    if (!message) {
      toast.error(TOAST_SENTIMENT_FAILED);
      return;
    }

    const data: ClientMessageSuicidalityReqDto = {
      content: message.content,
    };

    dispatch(
      setSuicidalityProcessing({
        userId: client.userId,
        messageId,
        suicidalityProcessing: true,
      })
    );

    const res = await checkResponse(
      fetchWithAuth(MESSAGE_SUICIDALITY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }),
      { toastOnApiError: true, toastOnFetchError: true }
    );
    if (res) {
      const body: ClientMessageSuicidalityResDto = await res.json();
      dispatch(
        setClientMessageSuicidality({
          userId: client.userId,
          messageId,
          contentSuicideLabel: body.label,
          contentSuicideScore: body.score,
        })
      );
    }
  };

export const saveChatsToDatabase =
  (
    bubbles: TraineeBubble[],
    bubbleInfo: TraineeBubbleInfo
  ): AppThunk<Promise<boolean>> =>
  async () => {
    if (bubbles.length === 0) {
      toast.warn(TOAST_NO_MESSAGES);
      return false;
    }

    const res = await checkResponse(
      fetchWithAuth(BUBBLES_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bubbles),
      }),
      { toastOnApiError: true, toastOnFetchError: true }
    );

    if (!res) return false;

    const body: TraineeBubble[] = await res.json();
    if (body.length === 0) {
      toast.error(CHAT_SAVE_TO_DATABASE_FAILED);
      return false;
    }

    const resInfo = await checkResponse(
      fetchWithAuth(BUBBLE_INFO_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bubbleInfo),
      }),
      { toastOnApiError: true, toastOnFetchError: true }
    );

    if (resInfo) {
      const info: TraineeBubbleInfo = await resInfo.json();
      toast.success(`${info.chatName} is successfully saved to the database!`);
      return true;
    } else {
      toast.error(CHAT_SAVE_TO_DATABASE_FAILED);
      return false;
    }
  };

export const deleteChatsToDatabase =
  (id: string): AppThunk =>
  async (dispatch, getState) => {
    let deleteUrl = new URL(`chat/bubble-info/${id}`, BASE_URL);
    const resInfo = await checkResponse(
      fetchWithAuth(deleteUrl, { method: 'DELETE' }),
      { toastOnApiError: true, toastOnFetchError: true }
    );

    if (resInfo) {
      const savedChats = selectSavedChatsDto(getState());
      const body: SavedChatDto = await resInfo.json();
      const filteredSavedChats = savedChats.filter(
        (item) => item.id !== body.id
      );
      dispatch(setSavedChats(filteredSavedChats));
      dispatch(
        setSelectedChat({
          id: '',
          created_at: '',
          updated_at: '',
          chatId: '',
          owner: '',
          chatName: '',
          feedback: '',
          reviewer: '',
          status: '',
          stage: '',
          notes: '',
        })
      );

      deleteUrl = new URL(`chat/bubbles/${body.chatId}`, BASE_URL);
      const resBubbles = await checkResponse(
        fetchWithAuth(deleteUrl, { method: 'DELETE' }),
        { toastOnApiError: true, toastOnFetchError: true }
      );

      if (resBubbles) {
        dispatch(setChatBubbles([]));
        const successMessage = `${body.chatName} is successfully deleted in the database!`;
        toast.success(successMessage);
      } else {
        toast.error(CHAT_SAVE_TO_DATABASE_FAILED);
      }
    }
  };

function mapDtoToMessageAssistant(
  dto: AssistantMessageDto
): ChatMessageWithAssistant {
  return {
    messageId: dto.id,
    role: dto.is_user ? 'user' : 'assistant',
    content: dto.content,
    createdAt: dto.created_at,
    modelTag: dto.model_tag,
    promptType: dto.prompt_type,
  };
}

function mapDtoToMessageClient(dto: ClientMessageDto): ChatMessageWithClient {
  return {
    messageId: dto.id,
    role: dto.is_user ? 'user' : 'assistant',
    content: dto.content,
    contentSentiment: dto.content_sentiment,
    historySentiment: dto.history_sentiment,
    createdAt: dto.created_at,
  };
}

function mapDtoToClient(dto: ChatWithMessagesDto): Client {
  return {
    userId: dto.chat_id,
    userType: 'client',
    username: dto.username,
    friendlyName: dto.first_name,
    isArchived: dto.archived,
  };
}

function mapDtoToActor(dto: ActorProfileDto): Actor {
  return {
    userId: dto.id,
    name: dto.name,
    userType: 'actor',
    prompt: dto.prompt,
    local_time_start: dto.local_time_start,
    level: dto.level,
  };
}

function mapDtoToChat(dto: ChatWithMessagesDto): Chat {
  const chat = createChat(dto.chat_id);
  for (const message of dto.assistant_messages?.map(mapDtoToMessageAssistant) ||
    []) {
    chat.messagesWithAssistant[message.messageId] = message;
  }
  for (const message of dto.client_messages?.map(mapDtoToMessageClient) || []) {
    chat.messagesWithClient[message.messageId] = message;
  }
  return chat;
}

function mapDtoToBotDetails(dto: BotDetailsDto): BotDetails {
  return {
    username: dto.username,
    friendly_name: dto.friendly_name,
    link: dto.link,
  };
}

function mapDtoToClientProfile(
  actor: ActorProfileDto,
  client: ClientProfileDto
): ClientProfile {
  return {
    age: client.age,
    gender: client.gender,
    address: client.address,
    ethnicity: client.ethnicity,
    language_preferred: client.language_preferred,
    ses: client.ses,
    religion: client.religion,
    occupation: client.occupation,
    accommodation: client.accommodation,
    relationship_status: client.relationship_status,
    id: client.id,
    created_at: client.created_at,
    updated_at: client.updated_at,
    summary: actor.summary,
    level: actor.level,
  };
}

export const listChats = (): AppThunk => async (dispatch) => {
  const res = await checkResponse(fetchWithAuth(CHATS_URL), {
    toastOnApiError: true,
    toastOnFetchError: true,
  });
  if (!res) {
    return;
  }
  const data: ChatWithMessagesDto[] = await res.json();
  dispatch(setClients(data.map(mapDtoToClient)));
  dispatch(setChats(data.map(mapDtoToChat)));
};

export const listActors = (): AppThunk => async (dispatch) => {
  const res = await checkResponse(fetchWithAuth(ACTORS_URL), {
    toastOnApiError: true,
    toastOnFetchError: true,
  });
  if (!res) {
    return;
  }
  const data: ActorProfileDto[] = await res.json();
  dispatch(setActors(data.map(mapDtoToActor)));
};

export const clientProfile =
  (userId: string | undefined, isActor: boolean): AppThunk =>
  async (dispatch, getState) => {
    if (userId) {
      const state = getState();
      const actors = selectActors(state);
      const username = selectUserAuth(state)?.username;

      // Resolve the actor ID from composite actor chat IDs like
      // "actorId_username" so profile calls always target /actor/profile/:actorId.
      let actorId: string | undefined;
      if (userId in actors) {
        actorId = userId;
      }
      if (!actorId && username) {
        const suffix = `_${username}`;
        if (userId.endsWith(suffix)) {
          const candidateActorId = userId.slice(0, -suffix.length);
          if (candidateActorId in actors) {
            actorId = candidateActorId;
          }
        }
      }
      if (!actorId) {
        actorId = Object.keys(actors)
          .sort((a, b) => b.length - a.length)
          .find((id) => userId === id || userId.startsWith(`${id}_`));
      }

      // For actor chats, fetch actor profile first to get actor.summary.
      if (isActor || actorId) {
        const actorProfileId = actorId || userId;
        const url = new URL(
          `actor/profile/${encodeURIComponent(actorProfileId)}`,
          BASE_URL
        );
        const res = await checkResponse(fetchWithAuth(url), {
          // This request can be a best-effort actor-chat detection.
          // Avoid noisy toasts and fall back to client profile if needed.
          toastOnApiError: false,
          toastOnFetchError: false,
        });
        if (res) {
          const actor: ActorProfileDto = await res.json();
          const client: ClientProfileDto = {
            age: actor.age.actual,
            gender: actor.gender,
            address: null,
            ethnicity: null,
            language_preferred: null,
            ses: null,
            religion: null,
            occupation: null,
            accommodation: null,
            relationship_status: null,
            id: actorProfileId,
            created_at: '',
            updated_at: '',
          };
          dispatch(setClientProfile(mapDtoToClientProfile(actor, client)));
          return;
        }
      }

      const url = new URL(
        `chat/${encodeURIComponent(userId)}/profile`,
        BASE_URL
      );
      const res = await checkResponse(fetchWithAuth(url), {
        toastOnApiError: true,
        toastOnFetchError: true,
      });
      if (!res) {
        return;
      }
      const client: ClientProfileDto = await res.json();
      const actor: ActorProfileDto = {
        id: '',
        name: '',
        prompt: '',
        summary: '',
        level: '',
        scale: 0,
        age: {
          actual: 0,
          min: 0,
          max: 0,
        },
        gender: 'undisclosed',
      };
      dispatch(setClientProfile(mapDtoToClientProfile(actor, client)));
    }
  };

export const updateClientProfile =
  (profile: ClientProfileRequest, chatId: string | undefined): AppThunk =>
  async (dispatch) => {
    const url = new URL(`chat/${chatId}/profile`, BASE_URL);
    const res = await checkResponse(
      fetchWithAuth(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profile),
      }),
      {
        toastOnApiError: true,
        toastOnFetchError: true,
      }
    );
    if (res) {
      const client: ClientProfileDto = await res.json();
      const actor: ActorProfileDto = {
        id: '',
        name: '',
        prompt: '',
        summary: '',
        level: '',
        scale: 0,
        age: {
          actual: 0,
          min: 0,
          max: 0,
        },
        gender: 'undisclosed',
      };
      dispatch(setClientProfile(mapDtoToClientProfile(actor, client)));
      toast.success('Client Profile updated!');
    }
  };

export const archiveChat =
  (chatId: string): AppThunk =>
  async (dispatch) => {
    const archiveUrl = new URL(`chat/${chatId}/archive`, BASE_URL);
    const res = await checkResponse(
      fetchWithAuth(archiveUrl, { method: 'POST' })
    );
    if (res) {
      const data: ChatWithMessagesDto = await res.json();
      dispatch(delClient(chatId));
      dispatch(setClient(mapDtoToClient(data)));
      dispatch(delChat(chatId));
      dispatch(setChat(mapDtoToChat(data)));
      toast.success(TOAST_ARCHIVED);
    }
  };

export const deleteChatWithAssistant =
  (chatId: string): AppThunk =>
  async (dispatch, getState) => {
    const client = selectCurrentClient(getState());
    if (!client) {
      return;
    }
    if (client.userType === 'client') {
      const deleteUrl = new URL(`chat/${chatId}/assistant`, BASE_URL);
      const res = await checkResponse(
        fetchWithAuth(deleteUrl, { method: 'DELETE' }),
        { toastOnApiError: true, toastOnFetchError: true }
      );
      if (res) {
        const data: ChatWithMessagesDto = await res.json();
        dispatch(setChat(mapDtoToChat(data)));
      }
    } else {
      dispatch(clearMessagesWithAssistant(client.userId));
    }
    toast.success('Conversation cleared!');
  };

export const deleteChatWithClient =
  (chatId: string): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const client = selectCurrentClient(state);
    if (!client) {
      return;
    }

    const username = selectUserAuth(state)?.username;
    const actors = selectActors(state);

    // Detect actor chat. This is a temparory measurement, needs long term fix @TODO
    // Two cases require different treatment:
    //   Fresh session:   currentClientId = plain actor ID (e.g. "heartbroken")
    //                    → client.userType === 'actor'
    //
    //   DB-restored:     currentClientId = composite key  (e.g. "heartbroken_alice")
    //                    → client.userType === 'client'  (mapDtoToClient always sets 'client')
    //                    → detected by checking that the chatId ends with "_username"
    //                      and the prefix is a known actor
    //
    let isActorChat = client.userType !== 'client';
    if (!isActorChat && username) {
      const suffix = '_' + username;
      if (chatId.endsWith(suffix)) {
        const actorId = chatId.slice(0, -suffix.length);
        if (actorId in actors) {
          isActorChat = true;
        }
      }
    }

    // Normalise to the composite key for the DB delete.
    // Fresh sessions supply the plain actor ID; DB-restored sessions already
    // supply the full composite key. Both are safe to run through this logic.
    let dbChatId = chatId;
    if (isActorChat && username) {
      const suffix = `_${username}`;
      if (!chatId.endsWith(suffix)) {
        dbChatId = `${chatId}${suffix}`;
      }
    }

    //Added encodeURIComponent() to the URL path so that email-style usernames (alice@example.com) in composite keys
    // are safely percent-encoded (%40) and correctly decoded by Litestar on the backend.
    const deleteUrl = new URL(
      `chat/${encodeURIComponent(dbChatId)}/client`,
      BASE_URL
    );
    const res = await checkResponse(
      fetchWithAuth(deleteUrl, { method: 'DELETE' }),
      { toastOnApiError: true, toastOnFetchError: true }
    );

    if (!isActorChat) {
      // Telegram client — only clean Redux if DB delete succeeded
      if (res) {
        dispatch(delClient(chatId));
        dispatch(delChat(chatId));
      }
    } else {
      // Actor chat — always clear Redux state even if DB had no prior record.
      // clearMessagesWithClient uses client.userId (may be plain or composite);
      // delClient/delChat use chatId (the key that was actually stored in Redux).
      dispatch(clearMessagesWithClient(client.userId));
      dispatch(clearMessagesWithAssistant(client.userId));
      dispatch(delClient(chatId));
      dispatch(delChat(chatId));
      dispatch(setCurrentClient(''));
      dispatch(deleteRiskOfChat(client.userId));
    }
    toast.success('Conversation cleared!');
  };

export const getAssistantMessageScoresThunk =
  (): AppThunk => async (dispatch) => {
    const res = await checkResponse(fetchWithAuth(MESSAGE_SCORE_URL), {
      toastOnApiError: true,
      toastOnFetchError: true,
    });
    if (!res) {
      return;
    }
    const data: AssistantMessageScoreDto[] = await res.json();
    dispatch(setAssistantMessageScores(data));
  };

export const setAssistantMessageScoreThunk =
  (dto: AssistantMessageScoreDto): AppThunk =>
  async (dispatch) => {
    const res = await checkResponse(
      fetchWithAuth(MESSAGE_SCORE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dto),
      })
      // { toastOnApiError: true, toastOnFetchError: true }
    );
    if (res?.ok) {
      dispatch(getAssistantMessageScoresThunk());
    } else {
      dispatch(setAssistantMessageScore(dto));
    }
  };

export const directChatForLetsTalk =
  (content: string): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const messagesWithAssistant = selectLetsTalkMessagesWithAssistant(state);
    const messagesWithClient = selectLetsTalkMessagesWithClient(state);

    dispatch(
      queryLetsTalkAssistant({
        chat_id: '', // not needed
        content,
        history_assistant: messagesWithAssistant,
        history_client: messagesWithClient,
        prompt_type: 'direct',
        persist: false,
      })
    );
  };

export const generateResponseForLetsTalk =
  (isLong: boolean): AppThunk =>
  async (dispatch, getState) => {
    const messagesWithClient = selectLetsTalkMessagesWithClient(getState());

    const incomingMessages = messagesWithClient.filter(
      (m) => m.role === 'assistant'
    );
    const n = incomingMessages.length;
    if (n === 0) {
      toast.warn(TOAST_NO_MESSAGES);
      return;
    }

    const content = incomingMessages[n - 1].content;

    dispatch(
      pushLetsTalkUserToAssistantMessage({
        userId: '', // ignored
        payload: isLong
          ? '___Propose long message___'
          : '___Propose short message___',
        messageId: uuidv4(),
      })
    );
    dispatch(
      queryLetsTalkAssistant({
        chat_id: '', // not needed
        content,
        history_client: messagesWithClient,
        prompt_type: isLong
          ? 'generate_lets_talk_response_befriender'
          : 'generate_response',
        persist: false,
      })
    );
    dispatch(
      queryLetsTalkAssistant({
        chat_id: '', // not needed
        content,
        history_client: messagesWithClient,
        prompt_type: isLong
          ? 'generate_lets_talk_response_therapist'
          : 'generate_response',
        persist: false,
      })
    );
  };

export const generateResourceForLetsTalk =
  (): AppThunk => async (dispatch, getState) => {
    const messagesWithClient = selectLetsTalkMessagesWithClient(getState());
    if (messagesWithClient.length === 0) {
      toast.warn(TOAST_NO_MESSAGES);
      return;
    }

    const content = messagesWithClient[messagesWithClient.length - 1].content;
    dispatch(
      pushLetsTalkUserToAssistantMessage({
        userId: '', // ignored
        payload: '___Recommend resources___',
        messageId: uuidv4(),
      })
    );
    dispatch(
      queryLetsTalkAssistant({
        chat_id: '', // not needed
        content,
        history_client: messagesWithClient,
        prompt_type: 'recommend_resource',
        persist: false,
      })
    );
  };

export const rewriteEmpatheticallyForLetsTalk =
  (): AppThunk => async (dispatch, getState) => {
    const state = getState();
    const messages = selectLetsTalkMessagesWithClient(state);
    const content = selectLetsTalkInputWithAssistant(state);
    if (!content) {
      toast.warn(TOAST_NO_INPUT);
      return;
    }

    dispatch(
      pushLetsTalkUserToAssistantMessage({
        userId: '', // ignored
        messageId: uuidv4(),
        payload: `___Rewrite empathetically___\n\n${content}`,
      })
    );
    dispatch(
      queryLetsTalkAssistant({
        chat_id: '', // not needed
        content,
        history_client: messages,
        prompt_type: 'empathetic_rewrite',
        persist: false,
      })
    );
    dispatch(setLetsTalkInputWithAssistant(''));
  };

export const botDetails = (): AppThunk => async (dispatch) => {
  const url = new URL(`chat/bot/telegram`, BASE_URL);
  const res = await checkResponse(fetchWithAuth(url), {
    toastOnApiError: false,
    toastOnFetchError: true,
  });
  if (!res) {
    return;
  }
  const data: BotDetailsDto = await res.json();
  dispatch(setBotDetails(mapDtoToBotDetails(data)));
};
